import BaseModel from './BaseModel';
import Project from './Project';
import Release from './Release';
import Sprint from './Sprint';
import Goal from './Goal';
import Log from './Log';
import User from './User';
import Commit from './Commit';
import Comment from './Comment';
import Quote from './Quote';
import { AnyObj } from '../types';
import _ from 'lodash';
import moment from 'moment';
import type { Moment } from 'moment';
import type { IconType } from '../components/Icon';
import Client from "./Client";
import { Changes } from "../services";

/** Class representing a change. */
class Change extends BaseModel {
    static emptyReplyRecipients() {
        return {
            to: { emails: [], signalUsers: [], signalGroups: [] },
            cc: { emails: [], signalUsers: [], signalGroups: [] }
        };
    }

    static emptyCompletionNotify() {
        return {
            emails: [],
            signalUsers: [],
            signalGroups: []
        };
    }
    /**
     * Change type constants
     */
    static TYPE_FEATURE  = 'feature';
    static TYPE_FIX      = 'fix';
    static TYPE_REFACTOR = 'refactor';
    static TYPE_UI       = 'ui';
    static TYPE_I18N     = 'i18n';
    static TYPE_TEST     = 'test';
    static TYPE_DOC      = 'doc';
    static TYPE_LINT     = 'lint';
    static TYPE_CHORE    = 'chore';
    static TYPE_BUILD    = 'build';
    static TYPE_DEPS     = 'dependencies';
    static TYPE_RELEASE  = 'release';
    static TYPE_IGNORED  = 'ignored';
    static TYPE_NONE     = 'none';  // to be used fof "non dev" tasks

    /**
     * Change progress status constants
     */
    static STATUS_PENDING_TRIAGE        = 'pendingTriage';
    static STATUS_PENDING_ESTIMATE      = 'pendingEstimate';
    static STATUS_ESTIMATED             = 'estimated';
    static STATUS_PENDING_PLANIFICATION = 'pendingPlanification';
    static STATUS_PLANNED               = 'planned';
    static STATUS_ASSIGNED              = 'assigned';
    static STATUS_IN_PROGRESS           = 'inProgress';
    static STATUS_TEAM_REVIEW           = 'teamReview';
    static STATUS_CLIENT_REVIEW         = 'clientReview';
    static STATUS_PENDING_DEPLOYMENT    = 'pendingDeployment';
    static STATUS_DEPLOYED              = 'deployed';
    static STATUS_FINISHED              = 'finished';
    static STATUS_SPAM_SUSPECT          = 'spam_suspect';
    static STATUS_DISCARDED             = 'discarded';
    static STATUS_MERGED                = 'merged';

    /**
     * Billing methods constants
     */
    static BILLING_METHOD_FIXED  = 'fixed';
    static BILLING_METHOD_HOURLY = 'hourly';

    /**
     * Importance constants
     */
    static IMPORTANCE_VERY_HIGH = 5;
    static IMPORTANCE_HIGH      = 4;
    static IMPORTANCE_NORMAL    = 3;
    static IMPORTANCE_LOW       = 2;
    static IMPORTANCE_VERY_LOW  = 1;
    static IMPORTANCE_ABANDONED = 0;

    /**
     * Issue priorities constants
     */
    static ISSUE_PRIORITY_LOW  = 'low';
    static ISSUE_PRIORITY_MED  = 'med';
    static ISSUE_PRIORITY_HIGH = 'high';

    _id: string;
    author?: AnyObj;
    createdAt?: string;
    updatedAt?: string;
    slug: number;
    release: Release;
    release_notes: {
        title: AnyObj;
        description: AnyObj;
        image?: string;
        extraImages?: { changeId: string }[];
    };
    sprints: Sprint[];
    goals: Goal[];
    /** Ids of Project.labels attached to this card. */
    labelIds: string[];
    related: Change[];
    assignee?: User;
    type: string;
    typeDomain?: string;
    typeSubtype?: string;
    /** Short plain/HTML excerpt (email intake / triage listings). */
    summary?: string;
    developer_notes?: string;
    technicalContext?: {
        sentry?: {
            culprit?: string;
            metadata?: AnyObj | null;
        };
        environment?: AnyObj;
        mergeReview?: {
            candidateId?: string;
            slug?: string;
            confidence?: number;
        };
        triage?: {
            spamRejected?: boolean;
            reason?: string;
        };
        rt?: {
            transactionIds?: string[];
            requestorEmails?: string[];
        };
        legacyNotes?: string;
    };
    intake?: {
        sender?: {
            email?: string;
            domain?: string;
        };
        queueAlias?: string;
        spamConfidence?: number;
        spamSource?: string;
        messageId?: string;
        inReplyTo?: string;
        references?: string[];
        contentHash?: string;
        headers?: {
            auth?: {
                spf?: string;
                dkim?: string;
                dmarc?: string;
            };
            from?: string;
            replyTo?: string;
            returnPath?: string;
            xSpamStatus?: string;
            xSpamScore?: string;
            replyToDiffersFrom?: boolean;
            returnPathDiffersFrom?: boolean;
            raw?: string;
        };
    };
    logs: Log[];
    commits: Commit[];
    comments: Comment[];
    project: Project;
    project_name: string;
    title: AnyObj;
    description: AnyObj;
    ticket_number?: string;
    ticket_view_url?: string;
    ticket_reply_url?: string;
    ticket_close_url?: string;
    nbAttachments: number;
    nbComments: number;
    deadline?: Moment;
    estimable: boolean;
    estimate: number;
    importance: number;
    priority: number;
    quote?: Quote;
    billable: boolean;
    billingMethod: string;
    estimateAsFloor: boolean;
    /** Hide from changelog without changing type. */
    omitFromChangelog: boolean;
    /** Client email reply awaiting team attention. */
    clientReplyPending: boolean;
    progress: {
        status: string;
        estimatedAt?: Moment;
        plannedAt?: Moment;
        assignedAt?: Moment;
        startedProgressAt?: Moment;
        startedTeamReviewAt?: Moment;
        startedClientReviewAt?: Moment;
        clientReviewAcceptedAt?: Moment;
        deployedAt?: Moment;
        finishedAt?: Moment;
        mergedAt?: Moment;
    };
    mergedInto?: {
        change?: string;
        project?: string;
        slug?: number;
        at?: Moment;
        by?: string;
    };
    client?: Client;
    issue?: {
        id: string;
        link: string;
        source: string;
    };
    replyRecipients: AnyObj;
    completionNotify: {
        emails: string[];
        signalUsers: string[];
        signalGroups: string[];
    };
    subscribers: string[];

    constructor(input: AnyObj | string) {
        super({});
        const properties = BaseModel.fromRef(input);

        this._id             = properties._id;
        this.author          = new User(properties.author?._id ? properties.author : { _id: properties.author });
        this.createdAt       = properties.createdAt;
        this.updatedAt       = properties.updatedAt;
        this.slug            = properties.slug;
        this.type            = properties.type;
        this.typeDomain      = properties.typeDomain;
        this.typeSubtype     = properties.typeSubtype;
        this.summary         = properties.summary;
        this.developer_notes = properties.developer_notes;
        this.technicalContext = properties.technicalContext;
        this.intake = properties.intake;
        this.project         = properties.project;
        if (_.isObject(this.project))
            this.project = new Project(this.project);

        this.project_name  = properties.project_name;
        this.mergedInto    = properties.mergedInto;
        this.title         = properties.title;
        this.description   = properties.description;
        this.ticket_number = properties.ticket_number;
        this.nbAttachments = properties.nbAttachments || 0;
        this.nbComments    = properties.nbComments || 0;

        this.release = properties.release;
        if (properties.release &&
            !properties.release._id &&
            properties.release.length > 0
        ) {
            this.release = new Release({
                _id: properties.release
            });
        }

        this.sprints  = (properties.sprints || []).map((s: AnyObj) => new Sprint(s));
        this.goals    = (properties.goals || []).map((g: AnyObj) => new Goal(_.isObject(g) ? g : {_id: g}));
        this.labelIds = (properties.labelIds || []).map((id: AnyObj) => _.isString(id) ? id : id?._id).filter(Boolean);
        this.related  = (properties.related || []).map((r: AnyObj) => {
            const raw = _.isObject(r) ? { ...r, related: [] } : { _id: r };
            return new Change(raw);
        });

        if (properties.assignee)
            this.assignee = new User(properties.assignee);

        if (properties.deadline)
            this.deadline = moment(properties.deadline);

        if (properties.quote)
            this.quote = new Quote(properties.quote);

        if (properties.issue)
            this.issue = properties.issue;
        const emptyReplyRecipients = Change.emptyReplyRecipients();
        this.replyRecipients = {
            to: {
                ...emptyReplyRecipients.to,
                ...(properties.replyRecipients?.to || {})
            },
            cc: {
                ...emptyReplyRecipients.cc,
                ...(properties.replyRecipients?.cc || {})
            }
        };
        const emptyCompletionNotify = Change.emptyCompletionNotify();
        this.completionNotify = {
            emails: (properties.completionNotify?.emails || emptyCompletionNotify.emails)
                .map((email: AnyObj) => _.isString(email) ? email.trim() : '')
                .filter(Boolean),
            signalUsers: (properties.completionNotify?.signalUsers || emptyCompletionNotify.signalUsers)
                .map((s: AnyObj) => _.isString(s) ? s : s?._id)
                .filter(Boolean),
            signalGroups: (properties.completionNotify?.signalGroups || emptyCompletionNotify.signalGroups)
                .map((s: AnyObj) => _.isString(s) ? s : s?._id)
                .filter(Boolean)
        };
        this.subscribers = (properties.subscribers || []).map((s: AnyObj) => _.isString(s) ? s : s?._id).filter(Boolean);

        this.estimable     = ('estimable' in properties ? !!properties.estimable : true);
        this.estimate      = properties.estimate || 0;
        this.importance    = properties.importance;
        this.priority      = properties.priority;
        this.billable         = ('billable' in properties ? !!properties.billable : true);
        this.billingMethod    = properties.billingMethod || Change.BILLING_METHOD_HOURLY;
        this.estimateAsFloor  = ('estimateAsFloor' in properties ? !!properties.estimateAsFloor : true);
        this.omitFromChangelog = !!properties.omitFromChangelog;
        this.clientReplyPending = !!properties.clientReplyPending;

        this.release_notes = properties.release_notes || {};
        if (!this.release_notes?.title)
            this.release_notes.title = {};
        if (!this.release_notes?.description)
            this.release_notes.description = {};

        this.progress = properties.progress;
        if (this.progress?.estimatedAt)
            this.progress.estimatedAt = moment(this.progress.estimatedAt);
        if (this.progress?.plannedAt)
            this.progress.plannedAt = moment(this.progress.plannedAt);
        if (this.progress?.assignedAt)
            this.progress.assignedAt = moment(this.progress.assignedAt);
        if (this.progress?.startedProgressAt)
            this.progress.startedProgressAt = moment(this.progress.startedProgressAt);
        if (this.progress?.startedTeamReviewAt)
            this.progress.startedTeamReviewAt = moment(this.progress.startedTeamReviewAt);
        if (this.progress?.startedClientReviewAt)
            this.progress.startedClientReviewAt = moment(this.progress.startedClientReviewAt);
        if (this.progress?.clientReviewAcceptedAt)
            this.progress.clientReviewAcceptedAt = moment(this.progress.clientReviewAcceptedAt);
        if (this.progress?.deployedAt)
            this.progress.deployedAt = moment(this.progress.deployedAt);
        if (this.progress?.finishedAt)
            this.progress.finishedAt = moment(this.progress.finishedAt);

        this.comments = properties.comments?.map((comment: AnyObj) => new Comment(comment));

        // virtuals
        this.logs    = (properties.logs || []).map((log: AnyObj) => new Log(log));
        this.commits = properties.commits?.map((commit: AnyObj) => new Commit(commit));
        this.client  = properties.client;
        if (_.isObject(this.client))
            this.client = new Client(this.client);
        if (properties.ticket_view_url)
            this.ticket_view_url = properties.ticket_view_url;
        if (properties.ticket_reply_url)
            this.ticket_reply_url = properties.ticket_reply_url;
        if (properties.ticket_close_url)
            this.ticket_close_url = properties.ticket_close_url;
    }

    static allStatus() {
        return [
            Change.STATUS_PENDING_TRIAGE,
            Change.STATUS_PENDING_ESTIMATE,
            Change.STATUS_ESTIMATED,
            Change.STATUS_PENDING_PLANIFICATION,
            Change.STATUS_PLANNED,
            Change.STATUS_ASSIGNED,
            Change.STATUS_IN_PROGRESS,
            Change.STATUS_TEAM_REVIEW,
            Change.STATUS_CLIENT_REVIEW,
            Change.STATUS_PENDING_DEPLOYMENT,
            Change.STATUS_DEPLOYED,
            Change.STATUS_FINISHED
        ];
    }

    statusIndex(status?: string) {
        return Change.allStatus().indexOf(status || this.progress?.status);
    }

    localizedTitle(lang: string) : string | null {
        if (!this.title)
            return null;

        return (lang in this.title && this.title[lang]) ? this.title[lang] : this.title[Object.keys(this.title)[0]];
    }

    localizedDescription(lang: string) : string | null {
        if (!this.description)
            return null;

        return (lang in this.description) ? this.description[lang] : this.description[Object.keys(this.description)[0]];
    }

    localizedReleaseTitle(lang: string) : string | null {
        if (!this.release_notes?.title)
            return null;

        return (lang in this.release_notes.title && this.release_notes.title[lang]) ?
            this.release_notes.title[lang] :
            this.release_notes.title[Object.keys(this.release_notes.title)[0]];
    }

    localizedReleaseDescription(lang: string) : string | null {
        if (!this.release_notes?.description)
            return null;

        return (lang in this.release_notes.description) ?
            this.release_notes.description[lang] :
            this.release_notes.description[Object.keys(this.release_notes.description)[0]];
    }

    /**
     * Check if this change is considered as started
     *
     * @param {Moment} at
     * @return {Boolean}
     */
    isStarted(at?: Moment): boolean {
        const startedStatus = [
            Change.STATUS_IN_PROGRESS,
            Change.STATUS_TEAM_REVIEW,
            Change.STATUS_CLIENT_REVIEW,
            Change.STATUS_PENDING_DEPLOYMENT,
            Change.STATUS_DEPLOYED,
            Change.STATUS_FINISHED,
            Change.STATUS_MERGED
        ];

        if (!startedStatus.includes(this.progress?.status))
            return false;

        if (!at)
            return true;

        if (!this.progress.startedProgressAt)
            return false;

        return this.progress.startedProgressAt.isBefore(at);
    }

    isMerged(): boolean {
        return this.progress?.status === Change.STATUS_MERGED;
    }

    /**
     * Check if this change is considered as finished, i.e iif
     * it's in one of the three following statuses:
     * - STATUS_PENDING_DEPLOYMENT
     * - STATUS_DEPLOYED
     * - STATUS_FINISHED
     *
     * @param {Moment} at
     * @return {Boolean}
     */
    isFinished(at?: Moment): boolean {
        const doneStatus = [
            Change.STATUS_PENDING_DEPLOYMENT,
            Change.STATUS_DEPLOYED,
            Change.STATUS_FINISHED,
            Change.STATUS_MERGED
        ];

        if (!doneStatus.includes(this.progress?.status))
            return false;

        if (!at)
            return true;

        // for non dev changes, we do not have the review step,
        // so let's check the finishedAt property
        if (!this.isDev()) {
            if (!this.progress.finishedAt)
                return false;

            return this.progress.finishedAt.isBefore(at);
        }

        if (!this.progress.clientReviewAcceptedAt)
            return false;

        return this.progress.clientReviewAcceptedAt.isBefore(at);
    }

    /**
     * Check if we have completed the work needed for this change,
     * i.e iif we are in a status "greater" than STATUS_IN_PROGRESS.
     *
     * Used in the sprints.
     *
     * @param {Moment} at
     * @return {Boolean}
     */
    isWorkCompleted(at?: Moment): boolean {
        // Non-dev: only finished counts as work completed (clientReview = waiting).
        if (!this.isDev()) {
            if (this.progress?.status !== Change.STATUS_FINISHED)
                return false;

            if (!at)
                return true;

            if (!this.progress.finishedAt)
                return false;

            return this.progress.finishedAt.isBefore(at);
        }

        if (this.statusIndex() <= this.statusIndex(Change.STATUS_IN_PROGRESS))
            return false;

        if (!at)
            return true;

        if (!this.progress.startedTeamReviewAt)
            return false;

        return this.progress.startedTeamReviewAt.isBefore(at);
    }

    /**
     * i18n key for the current progress status label.
     * Non-dev clientReview is shown as "waiting" rather than "in testing".
     */
    progressStatusLabelKey(): string {
        const status = this.progress?.status;
        if (!this.isDev() && status === Change.STATUS_CLIENT_REVIEW)
            return 'changes.progress_status_type_non_dev.clientReview';
        return `changes.progress_status_type.${status}`;
    }

    /**
     * Check if this change is a development task
     *
     * @return {Boolean}
     */
    isDev(): boolean {
        // Prefer typeDomain (canonical). Legacy intake cards may keep type
        // "support" (or other non-dev labels) while typeDomain is non_dev.
        if (this.typeDomain === 'non_dev')
            return false;
        if (this.typeDomain === 'dev')
            return true;
        // Legacy fallback: only canonical commit types are development work.
        return [
            Change.TYPE_FEATURE,
            Change.TYPE_FIX,
            Change.TYPE_UI,
            Change.TYPE_I18N,
            Change.TYPE_LINT,
            Change.TYPE_TEST,
            Change.TYPE_BUILD,
            Change.TYPE_REFACTOR,
            Change.TYPE_DOC,
            Change.TYPE_CHORE,
            Change.TYPE_DEPS,
            Change.TYPE_RELEASE,
        ].includes(this.type);
    }

    /**
     * Check if this change can be time tracked.
     * If by is provided, we check if this change can be
     * time tracked by the provided user.
     *
     * @param {User} by, optional
     * @return {Boolean}
     */
    isTimeTrackable(by?: User): boolean {
        if (!this.isStarted() || this.isFinished())
            return false;

        if (!by)
            return true;

        return this.isInReview(/*onlyByTeam*/true) || this.assignee?._id === by._id;
    }

    /**
     * Check whether this modification is under review (dev or customer)
     *
     * i@param {Boolean} onlyByTeam: true to check if this change is in review
     *                               by the team (ignore client review)
     * @return {Boolean}
     */
    isInReview(onlyByTeam?: boolean): boolean {
        return (
            (!onlyByTeam && this.progress?.status === Change.STATUS_CLIENT_REVIEW) ||
            this.progress?.status === Change.STATUS_TEAM_REVIEW
        );
    }

    /**
     * Check if this change matches a search string
     *
     * @param {String} search
     * @return {Boolean}
     */
    match(search: string) : boolean {
        let result: boolean = false;

        const sanitized = Change.sanitize(search);

        Object.keys(this.title).forEach((key: string) => {
            const sanitizedTitle = Change.sanitize(this.title[key]);
            result = result || !!sanitizedTitle.match(new RegExp(sanitized, 'gi'));
        })

        if (!result)
            result = !!this.slugName().match(new RegExp(sanitized, 'gi'));

        return result;
    }

    /**
     * Sanitize a string by replacing diacritics
     *
     * @param {String} str
     * @return {String} sanitized string
     */
    static sanitize(str: string) : string {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }

    /**
     * Get the date when this change finnished
     *
     * @return {Moment | undefined}
     */
    finishedAt(): Moment | undefined {
        if (!this.isFinished())
            return undefined;

        if (this.isDev())
            return this.progress.clientReviewAcceptedAt;

        return this.progress.finishedAt;
    }

    /**
     * Check if this change can be planned and assigned
     * - if it's not billable => true
     * - if it's billable
     *   - if it's billed hourly => true
     *   - if !needsAdvance and status is AcceptedQuote => true
     *   - if needsAdvance and status is PaidAdvance => true
     *   - else => false
     *
     * @return {Boolean}
     */
    isPlannable(): boolean {
        if (!this.billable)
            return true;

        if (this.isBilledHourly())
            return true;

        if (!this.quote)
            return false;

        const allowedStatus = [
            Quote.STATUS_ADVANCE_PAID,
            Quote.STATUS_BILLED,
            Quote.STATUS_PAID
        ];

        if (!this.quote.needsAdvance)
            allowedStatus.push(Quote.STATUS_ACCEPTED);

        return allowedStatus.includes(this.quote.status);
    }

    /**
     * Check if this change can be put into a sprint,
     * i.e if it's not finished and not already in a
     * not finished sprint
     *
     * @return {Boolean}
     */
    isSprintable(): boolean {
        if (this.isFinished() || this.isMerged())
            return false;

        return !this.sprints?.some((s: Sprint) => !s.isFinished());
    }

    /**
     * Check if this change can be put into a quote
     *
     * @return {Boolean}
     */
    isQuoteable(): boolean {
        return !this.quote;
    }

    /**
     * Check if this change is billed hourly
     *
     * @return {Boolean}
     */
    isBilledHourly(): boolean {
        return this.billingMethod === Change.BILLING_METHOD_HOURLY;
    }

    /**
     * Check if hourly billing should use the estimate as a minimum billable amount
     */
    usesEstimateAsFloor(): boolean {
        return !!(
            this.billable &&
            this.isBilledHourly() &&
            this.estimateAsFloor &&
            this.estimate > 0
        );
    }

    /**
     * Check if this change is a feature
     *
     * @return {Boolean}
     */
    isFeature() : boolean {
        return this.type === Change.TYPE_FEATURE;
    }

    /**
     * Check if this change is a bug fix
     *
     * @return {Boolean}
     */
    isBugFix() : boolean {
        return this.type === Change.TYPE_FIX;
    }

    slugName() : string {
        if (!this.isDev())
            return `S${this.slug}`;

        if (this.isFeature())
            return `F${this.slug}`;

        if (this.isBugFix())
            return `B${this.slug}`;

        return `C${this.slug}`;
    }

    formattedDate(){
        if (this.createdAt){
            return moment(this.createdAt).format("DD.MM.YYYY");
        }
    }

    /**
     * Get a value betwwen 0 and 100 representing the
     * progression of this change,
     *
     * @return {Number}
     */
    progression() : number {
        if (this.isFinished()) // 100%
            return 100;

        if (this.isInReview()) // 80%
            return 80;

        if (this.isStarted()) // 20%
            return 20;

        return 0;
    }

    iconName() : IconType { return Change.iconName(this.type); }

    /**
     * Get the list of possible changes types
     * @return {String[]}
     */
    static types() : string[] {
        return [
            Change.TYPE_FEATURE,
            Change.TYPE_FIX,
            Change.TYPE_UI,
            Change.TYPE_I18N,
            Change.TYPE_LINT,
            Change.TYPE_TEST,
            Change.TYPE_BUILD,
            Change.TYPE_REFACTOR,
            Change.TYPE_DOC,
            Change.TYPE_CHORE,
            Change.TYPE_DEPS,
            Change.TYPE_RELEASE,
            Change.TYPE_IGNORED,
            Change.TYPE_NONE
        ];
    }

    /**
     * Get the icon to represent the provided change type
     * @return {String}
     */
    static iconName(type: string) : IconType {
        switch (type) {
            case Change.TYPE_FEATURE:
                return 'feature';
            case Change.TYPE_FIX:
                return 'bug';
            case Change.TYPE_REFACTOR:
                return 'code';
            case Change.TYPE_UI:
                return 'ui';
            case Change.TYPE_I18N:
                return 'globe';
            case Change.TYPE_TEST:
                return 'test';
            case Change.TYPE_DOC:
                return 'comment';
            case Change.TYPE_LINT:
                return 'lint';
            case Change.TYPE_BUILD:
                return 'build';
            case Change.TYPE_RELEASE:
                return 'release';
            case Change.TYPE_CHORE:
                return 'chore';
            case Change.TYPE_NONE:
                return 'task';
            case Change.TYPE_IGNORED:
            default:
                return 'merge';
        }
    }

    color() : string { return Change.color(this.type); }

    /**
     * Get the color to represent the provided change type
     * @return {String}
     */
    static color(type: string) : string {
        switch (type) {
            case Change.TYPE_FEATURE:
                return 'purple';
            case Change.TYPE_FIX:
                return 'failure';
            case Change.TYPE_REFACTOR:
            case Change.TYPE_UI:
            case Change.TYPE_I18N:
            case Change.TYPE_DOC:
            case Change.TYPE_LINT:
                return 'indigo';
            case Change.TYPE_TEST:
                return 'info';
            case Change.TYPE_BUILD:
            case Change.TYPE_RELEASE:
                return 'success';
            case Change.TYPE_NONE:
                return 'lime';
            case Change.TYPE_CHORE:
            case Change.TYPE_IGNORED:
            default:
                return 'gray';
        }
    }

    issueIconName() : IconType {
        switch (this.issue?.source) {
            case 'sentry':
                return this.issue.source;
            default:
                return 'bug';
        }
    }

    /**
     * Prefer stored technicalContext; fall back to parsing legacy developer_notes.
     */
    resolvedTechnicalContext() {
        const stored = this.technicalContext;
        if (Change.hasTechnicalContextContent(stored))
            return stored;

        if (!this.developer_notes?.trim())
            return null;

        return Change.parseLegacyDeveloperNotes(this.developer_notes, this.issue);
    }

    hasTechnicalContext(): boolean {
        return Change.hasTechnicalContextContent(this.resolvedTechnicalContext())
            || Change.hasIntakeContent(this.intake);
    }

    static hasIntakeContent(intake?: Change['intake'] | null): boolean {
        if (!intake)
            return false;
        return !!(
            intake.sender?.email ||
            intake.sender?.domain ||
            intake.queueAlias ||
            intake.messageId ||
            intake.inReplyTo ||
            intake.contentHash ||
            intake.spamSource ||
            intake.headers?.raw ||
            intake.headers?.auth?.spf ||
            intake.headers?.auth?.dkim ||
            intake.headers?.auth?.dmarc ||
            (Array.isArray(intake.references) && intake.references.length > 0)
        );
    }

    static intakeEntries(intake?: Change['intake'] | null): Array<[string, string]> {
        if (!intake)
            return [];

        const entries: Array<[string, string]> = [];
        if (intake.sender?.email)
            entries.push(['senderEmail', intake.sender.email]);
        if (intake.sender?.domain)
            entries.push(['senderDomain', intake.sender.domain]);
        if (intake.queueAlias)
            entries.push(['queueAlias', intake.queueAlias]);
        if (intake.messageId)
            entries.push(['messageId', intake.messageId]);
        if (intake.inReplyTo)
            entries.push(['inReplyTo', intake.inReplyTo]);
        if (typeof intake.spamConfidence === 'number')
            entries.push(['spamConfidence', String(intake.spamConfidence)]);
        if (intake.spamSource)
            entries.push(['spamSource', intake.spamSource]);
        if (intake.contentHash)
            entries.push(['contentHash', intake.contentHash]);
        if (Array.isArray(intake.references) && intake.references.length > 0)
            entries.push(['references', intake.references.join(', ')]);

        return entries;
    }

    static hasTechnicalContextContent(technicalContext?: Change['technicalContext'] | null): boolean {
        if (!technicalContext)
            return false;
        if (technicalContext.legacyNotes?.trim())
            return true;
        if (technicalContext.sentry?.culprit || technicalContext.sentry?.metadata)
            return true;
        if (technicalContext.environment && Object.keys(technicalContext.environment).length > 0)
            return true;
        if (technicalContext.mergeReview?.candidateId)
            return true;
        if (technicalContext.triage?.spamRejected || technicalContext.triage?.reason)
            return true;
        return false;
    }

    static parseLegacyDeveloperNotes(text: string, issue?: Change['issue']) {
        const result: NonNullable<Change['technicalContext']> = {};
        const mergeReviewRe = /\[merge-review\]\s*candidate=(\S+)\s+slug=(\S+)\s+confidence=([\d.]+)/i;
        const spamRejectedRe = /\[spam-rejected\]\s*(.+)/i;
        const envHeaderRe = /^###\s*Informations d'environnement\s*/im;
        const sentryBlockRe = /^([^\n`]+)\n```\n([\s\S]*?)\n```\s*/;

        const humanLines: string[] = [];
        text.split("\n").forEach((line) => {
            const mergeMatch = line.match(mergeReviewRe);
            if (mergeMatch) {
                result.mergeReview = {
                    candidateId: mergeMatch[1],
                    slug: mergeMatch[2],
                    confidence: Number(mergeMatch[3])
                };
                return;
            }
            const spamMatch = line.match(spamRejectedRe);
            if (spamMatch) {
                result.triage = { spamRejected: true, reason: spamMatch[1].trim() };
                return;
            }
            humanLines.push(line);
        });

        let remaining = humanLines.join("\n").trim();
        if (envHeaderRe.test(remaining)) {
            const parts = remaining.split(envHeaderRe);
            const before = parts[0]?.trim();
            const envBlock = parts[1]?.trim() || "";
            const environment: AnyObj = {};
            envBlock.split(/\n\n+/).forEach((chunk) => {
                const separator = chunk.indexOf(":");
                if (separator === -1)
                    return;
                const key = chunk.slice(0, separator).trim();
                const value = chunk.slice(separator + 1).trim();
                if (key)
                    environment[key] = value;
            });
            if (Object.keys(environment).length > 0)
                result.environment = environment;
            remaining = before || "";
        }

        if (issue?.id) {
            const sentryMatch = remaining.match(sentryBlockRe);
            if (sentryMatch) {
                let metadata: AnyObj | null = null;
                try {
                    metadata = JSON.parse(sentryMatch[2]);
                } catch (err) {
                    metadata = { raw: sentryMatch[2] };
                }
                result.sentry = { culprit: sentryMatch[1].trim(), metadata };
                remaining = remaining.replace(sentryBlockRe, "").trim();
            } else if (remaining && !result.environment && !result.mergeReview && !result.triage) {
                result.sentry = {
                    culprit: remaining.split("\n")[0].trim(),
                    metadata: { raw: remaining }
                };
                remaining = "";
            }
        }

        if (remaining)
            result.legacyNotes = remaining;

        return result;
    }

    /**
     * Get the list of changes sortable properties.
     * This is used by the boards smart columns.
     *
     * @param {String} lang
     *
     * @return {Object[]} An array of objects with the following properties:
     *                    - property
     *                    - type
     *                    - label
     */
    static getSortableProperties(lang: string) {
        const properties = [
            [ 'slug' ],
            [ 'importance' ],
            [ 'priority' ],
            [ 'createdAt' ],
            [ 'deadline' ],
            [ 'estimate' ],
            [ 'type' ],
            [ `title.${lang}`, 'title' ],
            [ 'progress.status' ],
            [ 'progress.plannedAt' ],
            [ 'progress.assignedAt' ],
            [ 'progress.startedProgressAt' ],
            [ 'progress.startedTeamReviewAt' ],
            [ 'progress.startedClientReviewAt' ],
            [ 'progress.clientReviewAcceptedAt' ],
            [ 'progress.deployedAt' ],
            [ 'progress.finishedAt' ],
        ];

        return properties.map((props: any) => ({
            property: props[0],
            type: Change.getPropertyType(props[1] ?? props[0]),
            label: `changes.sort_properties.${props[1] ?? props[0]}`
        }));
    }

    /**
     * Get the list of changes filterable properties.
     * This is used by the boards smart columns.
     *
     * @param {String} lang
     *
     * @return {Object[]} An array of objects with the following properties:
     *                    - property
     *                    - type
     *                    - label
     */
    static getFilterableProperties(lang: string) {
        const properties = [
            [ 'project' ],
            [ 'client' ],
            [ 'assignee' ],
            [ 'slug' ],
            [ 'importance' ],
            [ 'priority' ],
            [ 'createdAt' ],
            [ 'deadline' ],
            [ 'estimate' ],
            [ 'type' ],
            [ `title.${lang}`, 'title' ],
            [ 'progress.status' ],
            [ 'progress.plannedAt' ],
            [ 'progress.assignedAt' ],
            [ 'progress.startedProgressAt' ],
            [ 'progress.startedTeamReviewAt' ],
            [ 'progress.startedClientReviewAt' ],
            [ 'progress.clientReviewAcceptedAt' ],
            [ 'progress.deployedAt' ],
            [ 'progress.finishedAt' ],
        ];

        return properties.map((props: any) => ({
            property: props[0],
            type: Change.getPropertyType(props[1] ?? props[0]),
            label: `changes.sort_properties.${props[1] ?? props[0]}`
        }));
    }

    /**
     * Get a property type.
     *
     * @param {String} property
     *
     * @return {String}
     */
    static getPropertyType(property: string) {
        switch (property) {
            case 'slug':
            case 'estimate':
            case 'importance':
            case 'priority':
                return 'number';

            case 'project':
            case 'client':
            case 'assignee':
            case 'type':
            case 'title':
            case 'progress.status':
                return 'string';

            case 'createdAt':
            case 'deadline':
            case 'progress.plannedAt':
            case 'progress.assignedAt':
            case 'progress.startedProgressAt':
            case 'progress.startedTeamReviewAt':
            case 'progress.startedClientReviewAt':
            case 'progress.clientReviewAcceptedAt':
            case 'progress.deployedAt':
            case 'progress.finishedAt':
                return 'date';
        }
    }

    /**
     * Get the list of smart columns filters operators.
     *
     * @return {Object[]} An array of objects with the following properties:
     *                    - op
     *                    - type
     *                    - label
     */
    static getFilterOperators(property?: string) {
        let operators = [
            ['$eq',          'scalar' ],
            ['$ne',          'scalar' ],
            ['$lt',          'scalar' ],
            ['$lte',         'scalar' ],
            ['$gt',          'scalar' ],
            ['$gte',         'scalar' ],
            ['$in',          'scalar' ],
            ['!$in',         'scalar' ],
            ['ISODate:$lt',  'date', '$lt' ],
            ['ISODate:$lte', 'date', '$lte' ],
            ['ISODate:$eq',  'date', '$eq' ],
            ['ISODate:$gt',  'date', '$gt' ],
            ['ISODate:$gte', 'date', '$gte' ],
        ];

        if (property) {
            const type = Change.getPropertyType(property);

            if (!['project', 'progress.status', 'type'].includes(property)) {
                operators = operators.filter((props: any) => {
                    return props[0] !== '$in' && props[0] !== '!$in';
                })
            }

            operators = operators.filter((props: any) => {
                if (type  === 'date')
                    return props[1] === 'date';

                return props[1] !== 'date';
            });
        }

        return operators.map((props: any) => ({
            property: props[0],
            type: props[1],
            label: `projects.column_rules.operators.${props[2] ?? props[0]}`
        }));
    }

    /**
     * Check if an operator supports multiple values
     *
     * @param {String} op
     *
     * @return {Boolean}
     */
    static isMultipleOperator(op: string) {
        return ['$in','!$in'].includes(op);
    }

    /**
     * Get the date possible values for smart filters
     *
     * @return {String[]}
     */
    static getDateFilterValues() {
        return [
            '-1:day',
            '-2:day',
            '-3:day',
            '-4:day',
            '-5:day',
            '-6:day',
            '-1:week',
            '-2:week',
            '-3:week',
            '-1:month',
            '-2:month',
            '-3:month',
            '-6:month',
            '-1:year',
            '1:day',
            '2:day',
            '3:day',
            '4:day',
            '5:day',
            '6:day',
            '1:week',
            '2:week',
            '3:week',
            '1:month',
            '2:month',
            '3:month',
            '6:month',
            '1:year'
        ];
    }

    /**
     * Get the possible values for the importance property
     *
     * @return {Number[]}
     */
    static getImportanceValues() {
        return [
            Change.IMPORTANCE_VERY_HIGH,
            Change.IMPORTANCE_HIGH,
            Change.IMPORTANCE_NORMAL,
            Change.IMPORTANCE_LOW,
            Change.IMPORTANCE_VERY_LOW,
            Change.IMPORTANCE_ABANDONED,
        ];
    }

    /**
     * Get the possible values for the issues priorities
     *
     * @return {String[]}
     */
    static getIssuesPrioritiesValues() {
        return [
            Change.ISSUE_PRIORITY_LOW,
            Change.ISSUE_PRIORITY_MED,
            Change.ISSUE_PRIORITY_HIGH
        ];
    }

    /**
     * Get this release note image url
     *
     * @return {String}
     */
    getReleaseImageUrl(size: number = 80, noCache: boolean = false) {
        if (this.release_notes?.image)
            return Change.releaseImageUrl(this._id, noCache);

        return null;
    }

    /**
     * All changelog images for this line, including extras kept from
     * omitted merged-project duplicates.
     */
    getReleaseImageUrls(noCache: boolean = false) : string[] {
        const urls: string[] = [];
        const primary = this.getReleaseImageUrl(80, noCache);
        if (primary)
            urls.push(primary);

        (this.release_notes?.extraImages || []).forEach((extra: { changeId?: string }) => {
            if (!extra?.changeId)
                return;
            urls.push(Change.releaseImageUrl(extra.changeId, noCache));
        });

        return urls;
    }

    static releaseImageUrl(changeId: string, noCache: boolean = false) : string {
        return [
            Changes.baseUrl,
            Changes.entryPoint,
            changeId,
            'releaseImage'
        ].join('/') + (noCache ? '?t=' + Date.now() : '');
    }

    /**
     * Get the (last) merge requets this change is linked to
     *
     * @return {Object}
     */
    getMergeRequest() {
        const log = this.logs?.find((log: Log) => log.type === Log.TYPE_CHANGE_TEAM_REVIEW_STARTED);
        if (!log?.context?.iid)
            return null;

        const { iid, url } = log.context;

        return { id: iid, link: url };
    }

    hasSubscriber(user?: User): boolean {
        if (!user?._id)
            return false;
        return (this.subscribers || []).includes(user._id);
    }
}

export default Change;
